// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xParserOptions } from "@steiner/XDL/Utilities/BasicSelectors";
import parse from "html-react-parser";
import React, { FC } from "react";

const enum fields {
    backgroundColor = "backgroundColor",
    contentPosition = "contentPosition",
    wrapperPaddingOnLargeScreen = "wrapperPaddingOnLargeScreen",
    wrapperPaddingOnSmallScreen = "wrapperPaddingOnSmallScreen",
    gapBetweenOnLargeScreen = "gapBetweenOnLargeScreen",
    gapBetweenOnSmallScreen = "gapBetweenOnSmallScreen",
    contentPaddingOnWideScreen = "contentPaddingOnWideScreen",
    contentPaddingOnLargeScreen = "contentPaddingOnLargeScreen",
    contentPaddingOnSmallScreen = "contentPaddingOnSmallScreen",
    contentDescription = "contentDescription",
    buttonLink = "buttonLink",
    buttonLabel = "buttonLabel",
    buttonVariant = "buttonVariant",
    additionalButtonLink = "additionalButtonLink",
    additionalButtonLabel = "additionalButtonLabel",
    additionalButtonVariant = "additionalButtonVariant",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundColor]: string;
        [fields.contentPosition]: string;
        [fields.wrapperPaddingOnLargeScreen]: string;
        [fields.wrapperPaddingOnSmallScreen]: string;
        [fields.gapBetweenOnLargeScreen]: string;
        [fields.gapBetweenOnSmallScreen]: string;
        [fields.contentPaddingOnWideScreen]: string;
        [fields.contentPaddingOnLargeScreen]: string;
        [fields.contentPaddingOnSmallScreen]: string;
        [fields.contentDescription]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonLabel]: string;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
        [fields.additionalButtonLink]: LinkFieldValue;
        [fields.additionalButtonLabel]: string;
        [fields.additionalButtonVariant]: "primary" | "secondary" | "tertiary";
    };
}

type Props = OwnProps;

export interface AboutBlockStyles {
    container?: InjectableCss;
    sourceWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    contentDescription?: TypographyPresentationProps;
    buttonWrapper?: InjectableCss;
    button?: ButtonPresentationProps;
    additionalButton?: ButtonPresentationProps;
}

export const aboutBlockStyles: AboutBlockStyles = {};

const styles = aboutBlockStyles;
const AboutBlock: FC<Props> = ({
    id,
    fields: {
        backgroundColor,
        contentPosition,
        wrapperPaddingOnLargeScreen,
        wrapperPaddingOnSmallScreen,
        gapBetweenOnLargeScreen,
        gapBetweenOnSmallScreen,
        contentPaddingOnWideScreen,
        contentPaddingOnLargeScreen,
        contentPaddingOnSmallScreen,
        contentDescription,
        buttonLink,
        buttonLabel,
        buttonVariant,
        additionalButtonLink,
        additionalButtonLabel,
        additionalButtonVariant,
    },
}) => {
    const { url: primaryUrl, title: primaryTitle } = useGetLink(buttonLink);
    const { url: additionalUrl, title: additionalTitle } = useGetLink(additionalButtonLink);
    const history = useHistory();
    const { isInShell } = useShellContext();

    const onClick = (link?: string) => {
        if (!link) {
            return;
        }

        if (link.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = link;
        } else {
            history.push(link);
        }
    };

    return (
        <StyledWrapper
            {...styles.container}
            {...{
                backgroundColor,
                contentPosition,
                wrapperPaddingOnLargeScreen,
                wrapperPaddingOnSmallScreen,
                gapBetweenOnLargeScreen,
                gapBetweenOnSmallScreen,
            }}
        >
            <StyledWrapper {...styles.sourceWrapper}>
                <Zone contentId={id} zoneName="Content Source Image Or Video" />
            </StyledWrapper>
            <StyledWrapper
                {...styles.contentWrapper}
                {...{ contentPaddingOnWideScreen, contentPaddingOnLargeScreen, contentPaddingOnSmallScreen }}
            >
                <Typography {...styles.contentDescription}>{parse(contentDescription, xParserOptions)}</Typography>
                <StyledWrapper {...styles.buttonWrapper}>
                    {(buttonLabel || primaryTitle) && (
                        <Button {...styles.button} variant={buttonVariant} onClick={() => onClick(primaryUrl)}>
                            {buttonLabel || primaryTitle}
                        </Button>
                    )}
                    {(additionalButtonLabel || additionalTitle) && (
                        <Button
                            {...styles.additionalButton}
                            variant={additionalButtonVariant}
                            onClick={() => onClick(additionalUrl)}
                        >
                            {additionalButtonLabel || additionalTitle}
                        </Button>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: AboutBlock,
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#ffffff",
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
            },
            {
                name: fields.contentPosition,
                displayName: "Content Position",
                editorTemplate: "DropDownField",
                options: [
                    { value: "row", displayName: "Position Right" },
                    { value: "row-reverse", displayName: "Position Left" },
                ],
                defaultValue: "row",
                fieldType: "General",
                sortOrder: 2,
                tab: basicTab,
            },
            {
                name: fields.wrapperPaddingOnLargeScreen,
                displayName: "Wrapper Padding On Large Screen",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "0",
                placeholder: "Enter: 5px, 5px auto, ...etc",
                validate: (value: string | null) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("padding", value) ? null : `Invalid Input`;
                },
                sortOrder: 3,
                tab: basicTab,
            },
            {
                name: fields.wrapperPaddingOnSmallScreen,
                displayName: "Wrapper Padding On Small Screen",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "0",
                validate: (value: string | null) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("padding", value) ? null : `Invalid Input`;
                },
                sortOrder: 4,
                tab: basicTab,
            },
            {
                name: fields.gapBetweenOnLargeScreen,
                displayName: "Gap Between On Large Screen",
                editorTemplate: "TextField",
                defaultValue: "0",
                fieldType: "General",
                validate: (value: string | null) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("gap", value) ? null : `Invalid Input`;
                },
                sortOrder: 5,
                tab: basicTab,
            },
            {
                name: fields.gapBetweenOnSmallScreen,
                displayName: "Gap Between On Small Screen",
                editorTemplate: "TextField",
                defaultValue: "0",
                fieldType: "General",
                validate: (value: string | null) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("gap", value) ? null : `Invalid Input`;
                },
                sortOrder: 6,
                tab: basicTab,
            },
            {
                name: fields.contentPaddingOnWideScreen,
                displayName: "Content Padding On Wide Screen",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "0",
                placeholder: "Enter: 5px, 5px auto, ...etc",
                validate: (value: string | null) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("padding", value) ? null : `Invalid Input`;
                },
                sortOrder: 7,
                tab: basicTab,
            },
            {
                name: fields.contentPaddingOnLargeScreen,
                displayName: "Content Padding On Large Screen",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "0",
                placeholder: "Enter: 5px, 5px auto, ...etc",
                validate: (value: string | null) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("padding", value) ? null : `Invalid Input`;
                },
                sortOrder: 8,
                tab: basicTab,
            },
            {
                name: fields.contentPaddingOnSmallScreen,
                displayName: "Content Padding On Small Screen",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "0",
                validate: (value: string | null) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("padding", value) ? null : `Invalid Input`;
                },
                sortOrder: 9,
                tab: basicTab,
            },
            {
                name: fields.contentDescription,
                displayName: "Content Description",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 10,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
                sortOrder: 11,
                tab: basicTab,
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "Contextual",
                sortOrder: 12,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                sortOrder: 13,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.additionalButtonLabel,
                editorTemplate: "TextField",
                displayName: "Additional Button Label",
                defaultValue: "",
                sortOrder: 14,
                tab: basicTab,
            },
            {
                name: fields.additionalButtonVariant,
                displayName: "Additional Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "Contextual",
                sortOrder: 15,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.additionalButtonLink,
                displayName: "Additional Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                sortOrder: 16,
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
